import {
  AxisSide,
  CellValue,
  ChartConfig,
  QueryResponse,
  ColumnDescription,
  ColumnDescriptor,
  FieldMetaCtx,
  getFieldMeta,
  GraphicEncoding,
  SeriesMark,
  parseOutputFieldRef,
  resolveBreakdownRef,
  resolveColumnDescription,
  resolveComparison,
  StatCompareMode,
  TableChartConfig,
  TableColumn,
  TableEncoding,
  totalsIgnoresMetricFilters,
  ValueFormat,
  withDerivedTotals
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import {
  colOrMetricRequiresKpi,
  ColumnOrMetricName,
  getScpConfig,
  requiresCustomAggregation
} from '@blisspointmedia/bpm-types/dist/Columns';
import {
  grainShape,
  isRetailMetricsQuery,
  QuerySpec
} from '@blisspointmedia/bpm-types/dist/querying';
import { DimensionId, OutputFieldRef } from '@blisspointmedia/bpm-types/dist/querying/ids';
import { QueryConfig } from '@blisspointmedia/bpm-types/dist/querying/queryConfig';
import {
  abbreviateNumber,
  CURRENCY_FORMATTER_MAP,
  formatMixedCurrency,
  formatNumber,
  formatNumberAsInt,
  toPrettyPercent,
  ZERO_LABEL
} from '@blisspointmedia/bpm-types/dist/formatUtils';
import { CURRENCY_SYMBOL_MAP } from '@blisspointmedia/bpm-types/dist/CurrencySymbols';
import { titleize } from 'inflected';
import { equals } from 'ramda';
import { getNiceTickValues } from 'recharts';
import { Neutral400 } from '@src/utils/colors';
import { SERIES_PALETTE } from './chart-renderer-constants';
import { buildColumnHeatStyle, HeatStyle } from './table-heatmap-utils';

// One resolved value series, ready to feed a recharts <Line>. `key` is the OutputFieldRef
// (e.g. "q0.spend") used as both the row data key and the recharts dataKey.
export interface PreparedSeries {
  key: string;
  label: string;
  // The name of the metric this series plots, for the value-axis title. Differs from `label` on a
  // color split (label is the breakdown value, e.g. "Facebook") and on a comparison (label carries the
  // "· current"/"· prior" suffix).
  metricLabel: string;
  color?: string;
  // Explicit per-series ValueFormat (used only by the value axis for its abbreviate-vs-full decision).
  format?: ValueFormat;
  // Registry-aware value formatter (currency/metric-correct), bound at prep via getFieldMeta. Used for
  // tooltips/legend values; the axis keeps `format` so its abbreviation behavior is unchanged.
  formatValue: (value: CellValue) => string;
  // Per-series mark override (combo charts); falls back to the chart's base mark when unset.
  mark?: SeriesMark;
  // Draw this series as a dashed line/area (e.g. a comparison-period overlay).
  dashed?: boolean;
  // Which value axis this series binds to (dual-axis charts). Defaults to the left axis.
  side?: AxisSide;
  // A combo series drawn on top of a color split (e.g. a revenue line over spend bars split by
  // channel). Never stacks with the split series, and takes the overlay palette.
  overlay?: boolean;
  // Period-over-period role, set only on comparison charts (resolveComparison): the series' query is
  // the base window ('current') or the prior window ('prior'). Drives the "· current/prior" label
  // suffix, the dashed prior overlay, and the tooltip's period tagging. Unset on non-comparison series.
  period?: ComparisonPeriod;
  // Scatter (point) only: this series' own point rows, handed straight to its <Scatter data=...>. Used
  // to render a period-over-period comparison as two separate clouds sharing one numeric x/y axis —
  // each cloud is its own series with its own points. Unset for a single-cloud scatter and every
  // non-scatter series, which read from the chart-level `rows` instead.
  points?: Record<string, CellValue>[];
  // Scatter comparison only: the cloud's group name (e.g. "This period"/"Prior period") shown in the
  // legend and tooltip — distinct from `label`, which stays the metric name for the value row.
  pointGroupLabel?: string;
}

// A GraphicEncoding chart flattened from the columnar QueryResponse into the row-array
// shape recharts wants, plus the metadata the renderer needs to format axes/tooltips.
export interface PreparedGraphicChart {
  rows: Record<string, CellValue>[];
  xKey: string;
  xFormat?: ValueFormat;
  xLabel?: string;
  // Registry-aware formatter for a numeric (scatter) x value, bound at prep; used by the scatter tooltip.
  xFormatValue?: (value: CellValue) => string;
  xIsDate: boolean;
  // True when x is a continuous measure (scatter): the renderer draws a numeric x-axis over the data
  // range instead of a category axis of discrete values. Line/bar/area leave it false (x is a date or
  // category).
  xIsNumeric?: boolean;
  series: PreparedSeries[];
  // For a scatter: the row key holding the per-point label (e.g. campaign), shown in the tooltip
  // instead of fanning the color dimension into a series per value.
  pointLabelKey?: string;
  // For a period-over-period overlay whose series all plot the SAME metric: that metric's name (e.g.
  // "Spend"), shown once as the tooltip header (the per-period series are distinguished by their day).
  comparisonMetricLabel?: string;
  // For a period-over-period overlay: the row key holding the PRIOR window's own x value (its date),
  // so the tooltip can show both windows' dates. The current window's date is the row's x.
  priorXKey?: string;
  // A short user-facing summary when data was capped for safety (too many series/points).
  notice?: string;
  // One metric fanned into a series per color value (not a comparison pair split).
  colorSplit?: boolean;
}

// Reserved row key under which a scatter point carries its color/label value (e.g. campaign name)
// for the tooltip. Prefixed so it can't collide with an OutputFieldRef (which is "q<n>.<name>").
const POINT_LABEL_KEY = '__pointLabel';

// Canonical row keys for a period-over-period scatter's two clouds. Both clouds share one numeric x
// and y axis, so their point rows expose coordinates under the SAME keys (each cloud is still handed
// to its own <Scatter> via `series.points`). A single-cloud scatter keeps its encoding refs as keys.
const SCATTER_X_KEY = '__scatterX';
// Reserved row key under which a period-over-period row carries the prior window's x (see priorXKey).
const PRIOR_X_KEY = '__priorX';
const SCATTER_Y_KEY = '__scatterY';

interface ResolvedColumn {
  values: CellValue[];
  descriptor?: ColumnDescriptor;
  // The response block the column was read from: a merged block ("q0+q1") or a single query's.
  blockId: string;
}

// Find the columnar array for an OutputFieldRef. A qualified ref ("q0.spend") lives in its query's
// block; a bare dimension name ("platform", from a breakdown control) is unqualified, so scan every
// block. Every produced ref (including the shared merge key) is always query-qualified now, so the
// unqualified path only ever matches a bare dimension NAME being looked up, not a merge key.
const resolveColumn = (data: QueryResponse, ref: string): ResolvedColumn | null => {
  const { queryId } = parseOutputFieldRef(ref);
  const block = queryId ? data.queries[queryId] : undefined;
  const candidates = block ? [[queryId, block] as const] : Object.entries(data.queries);
  for (const [blockId, candidate] of candidates) {
    if (candidate && Object.hasOwn(candidate.data, ref)) {
      return {
        values: candidate.data[ref],
        descriptor: candidate.schema.find(c => c.id === ref),
        blockId
      };
    }
  }
  return null;
};

// A breakdown control names its dimension bare ("platform"); the payload keys it query-qualified
// ("q0.platform"), and encoding.color is already qualified. Return the qualified ref that exists
// in the payload for a possibly-bare dimension ref, or null when the dimension wasn't fetched.
const qualifyColorRef = (data: QueryResponse, ref: string): string | null => {
  if (resolveColumn(data, ref)) return ref;
  const { colId } = parseOutputFieldRef(ref);
  for (const block of Object.values(data.queries)) {
    const match = block?.schema.find(
      c => c.role === 'dimension' && parseOutputFieldRef(c.id).colId === colId
    );
    if (match) return match.id;
  }
  return null;
};

// The config to FETCH for a breakdown on `active`. Saved configs group by every breakdown option (the
// validator requires it), so on the query feeding encoding.color (else the first), and on any query
// that groups by every option (a comparison window's copy), the other options are dropped and `active`
// is grouped. Every pick is therefore its own fetch, and the payload's breakdown is the active one
// only. The x axis and merge join keys are never dropped (canNarrowTo then rules the pick out); an
// orderBy term on a dropped dimension goes with it. Untagged metric filters follow the breakdown
// (they test at the rows shown). No-op without a breakdown control. Pure.
export const withActiveBreakdown = (config: ChartConfig, active: DimensionId): ChartConfig => {
  if (config.type === 'table' || config.type === 'stat') return config;
  const control = resolveBreakdownRef(config)?.control;
  if (control?.target.kind !== 'breakdown') return config;
  const { options } = control.target;
  const kept = new Set<string>([
    active,
    parseOutputFieldRef(config.encoding.x.ref).colId,
    ...(config.merge?.joinOn ?? []).map(key => key.id)
  ]);
  const dropped = new Set<string>(options.filter(opt => !kept.has(opt)));
  const colorRef = config.encoding.color?.ref;
  const targetId = colorRef ? parseOutputFieldRef(colorRef).queryId : config.queries[0]?.queryId;
  return {
    ...config,
    queries: config.queries.map(q => {
      const groupsByEveryOption = options.every(opt => q.dimensions.some(d => d.id === opt));
      if (q.queryId !== targetId && !groupsByEveryOption) return q;
      const dimensions = q.dimensions.filter(d => !dropped.has(d.id));
      if (!dimensions.some(d => d.id === active)) {
        dimensions.push({ id: active });
      }
      return { ...q, dimensions, orderBy: q.orderBy?.filter(term => !dropped.has(term.id)) };
    })
  };
};

// Can the chart be fetched narrowed to `opt`? Every query withActiveBreakdown narrows must come back
// grouped by nothing but x and `opt`, so the color split pivots 1:1 (an extra dimension — a merge join
// key, a stray saved dimension — would make duplicate (x, breakdown) rows overwrite each other).
// Its grain-tagged metric filters must keep their meaning: dropping dimensions can leave a "split"
// grain outside them (invalid), and Retail Metrics accepts only a redundant grain. A paged query must
// keep an orderBy term.
const canNarrowTo = (config: ChartConfig, opt: DimensionId): boolean => {
  if (config.type === 'table' || config.type === 'stat') return false;
  const pivotDims = new Set<string>([parseOutputFieldRef(config.encoding.x.ref).colId, opt]);
  const narrowed = withActiveBreakdown(config, opt).queries;
  return narrowed.every((q, i) => {
    if (q === config.queries[i]) return true;
    const breaksGrain = (q.metricFilters ?? []).some(f => {
      if (!f?.grain) return false;
      const shape = grainShape(q, f.grain);
      return shape === 'invalid' || (isRetailMetricsQuery(q) && shape !== 'redundant');
    });
    const losesPageOrder = q.page !== undefined && !q.orderBy?.length;
    return q.dimensions.every(d => pivotDims.has(d.id)) && !breaksGrain && !losesPageOrder;
  });
};

// The dimensions a chart's declared breakdown control offers, or none if it declares no control.
// The only source of truth for whether a breakdown is pickable ON this chart — which matters for a
// selection that didn't come from the picker (one carried in on a link, say, made against an older
// version of the chart's controls). Leaves out an option the chart can't be fetched narrowed to
// (canNarrowTo).
export const breakdownOptionsFor = (config: ChartConfig): DimensionId[] => {
  const control = resolveBreakdownRef(config)?.control;
  if (control?.target.kind !== 'breakdown') return [];
  return control.target.options.filter(opt => canNarrowTo(config, opt));
};

// The breakdown a chart is fetched and pivoted by: the viewer's pick when it is on offer, else the
// control's default when it is, else the first option on offer. undefined when the chart has no
// breakdown control or none of its options can be fetched narrowed (the chart then fetches as saved).
export const activeBreakdownFor = (
  config: ChartConfig,
  pick?: DimensionId | null
): DimensionId | undefined => {
  const offered = breakdownOptionsFor(config);
  const candidates = [pick, resolveBreakdownRef(config)?.ref, offered[0]];
  return candidates.find((dim): dim is DimensionId => dim != null && offered.includes(dim));
};

// Declaration-first color/breakdown resolution, highest precedence first:
//   1. a live picker selection (breakdownOverride — React view state, never written back to config)
//   2. a declared `breakdown` control's dimension (resolveBreakdownRef)
//   3. an explicit encoding.color
// No inference tail (Phase 3 Round B deleted inferColorRef): a color split must be DECLARED, either
// via a breakdown control or encoding.color. Control/override dims are bare, so they're qualified
// against the payload; an unfetched dim is skipped.
const resolveColorRef = (
  config: ChartConfig,
  data: QueryResponse,
  breakdownOverride?: string
): string | null => {
  if (config.type === 'table' || config.type === 'stat') return null;
  const { encoding } = config;

  // A breakdown control splits only a chart that can be fetched narrowed to one of its options: one
  // that can't comes back grouped by every option, which no 1:1 pivot can show.
  if (!activeBreakdownFor(config)) return encoding.color?.ref ?? null;
  if (breakdownOverride) {
    const qualified = qualifyColorRef(data, breakdownOverride);
    if (qualified) return qualified;
  }
  // Then the control's default, then any other option the payload carries: while a pick's refetch is
  // in flight the previous payload stays on screen, grouped by the previous pick.
  const control = resolveBreakdownRef(config)?.control;
  if (control?.target.kind === 'breakdown') {
    const defaultFirst = [control.default, ...control.target.options];
    for (const dim of defaultFirst) {
      const qualified = typeof dim === 'string' ? qualifyColorRef(data, dim) : null;
      if (qualified) return qualified;
    }
  }
  return encoding.color?.ref ?? null;
};

// The registry name behind a metric ref, for requiresCustomAggregation:
// "q0.roas" -> "roas", "q0.spend" -> "spend".
const metricName = (ref: string): string => parseOutputFieldRef(ref).colId;

// Period-over-period role of a series' query. 'current' = the comparison base window, 'prior' = the
// compare window. Purely a render/label concept; the mechanics stay the declared two-query + ordinal
// merge (see resolveComparison / ComparisonIntent in bpm-types).
export type ComparisonPeriod = 'current' | 'prior';

// The minimal shape prepareGraphicChart needs off resolveComparison to map a query to its period.
interface ComparisonRoles {
  base: string;
  prior: string;
}

// Which comparison window a ref belongs to, from its query prefix ("q0.spend" -> base). undefined when
// there's no comparison or the ref is neither the base nor prior query (a stray series is left untagged).
const getComparisonPeriod = (
  ref: string,
  comparison?: ComparisonRoles
): ComparisonPeriod | undefined => {
  if (!comparison) return undefined;
  const { queryId } = parseOutputFieldRef(ref);
  if (queryId === comparison.base) return 'current';
  if (queryId === comparison.prior) return 'prior';
  return undefined;
};

// Suffix a series' base name with its period so the legend + tooltip always read "which window":
// "Meta · current" / "Spend · prior". Uniform across breakdown and plain comparison charts.
const withPeriod = (name: string, period: ComparisonPeriod): string => `${name} · ${period}`;

// Row-key suffix marking a breakdown series' PRIOR value, so the current ("Meta") and prior clouds of
// one breakdown value get distinct recharts dataKeys. The NUL prefix can't appear in a dimension value.
const PRIOR_KEY_SUFFIX = '\u0000prior';

const NULL_SERIES_LABEL = '—';

// Defensive UI sort: the data source may not return rows in date order. When the x / primary
// dimension is a date, sort rows ascending by its RAW value — ISO "YYYY-MM-DD", which sorts
// correctly lexically — BEFORE any "M/D" formatting. Non-date axes (e.g. platform) keep their order.
// Mirrors what the query should also do; this is the belt-and-suspenders layer.
const sortRowsByDateKey = (
  rows: Record<string, CellValue>[],
  dateKey: string
): Record<string, CellValue>[] =>
  [...rows].sort((a, b) => {
    const av = a[dateKey];
    const bv = b[dateKey];
    if (typeof av !== 'string' || typeof bv !== 'string') return 0;
    if (av < bv) return -1;
    return av > bv ? 1 : 0;
  });

// Hard render caps. A high-cardinality breakdown (e.g. one line per campaign) would otherwise fan
// into thousands of recharts <Line>/<Bar> SVG nodes and OOM the browser tab. MAX_SERIES bounds the
// number of series (the expensive components); MAX_POINTS bounds rows per chart. Tuned for
// readability too — 25 series / 500 points is already past what's legible.
const MAX_SERIES = 25;
const MAX_POINTS = 500;

// Build the "+N more hidden" caption shown when a chart's data was capped. Returns undefined when
// nothing was dropped.
const buildNotice = (hiddenSeries: number, hiddenPoints: number): string | undefined => {
  const parts: string[] = [];
  if (hiddenSeries > 0) {
    parts.push(`+${hiddenSeries} more series`);
  }
  if (hiddenPoints > 0) {
    parts.push(`+${hiddenPoints} more points`);
  }
  return parts.length ? `Showing top results — ${parts.join(', ')} hidden` : undefined;
};

// Pivot the "long" columnar response (one row per x × color value) into the "wide" shape recharts
// needs for a series per color value: distinct x = rows, distinct color value = series, with
// row[colorValue] = the metric cell. Series are ordered by descending total so the largest read
// first. Splits the FIRST y metric (color fans a single measure by the contract). Returns null if
// the color/metric columns can't be resolved, so the caller falls back to the per-metric path.
const prepareColorSplit = (
  data: QueryResponse,
  encoding: GraphicEncoding,
  xColumn: ResolvedColumn,
  colorRef: string,
  ctx?: FieldMetaCtx
): PreparedGraphicChart | null => {
  const colorColumn = resolveColumn(data, colorRef);
  const yChannel = encoding.y[0];
  const yColumn = yChannel ? resolveColumn(data, yChannel.ref) : null;
  if (!colorColumn || !yChannel || !yColumn) return null;
  // Every split series plots the SAME y metric, so they share its formatter; the per-series label is
  // the color VALUE (e.g. "Facebook"), not the metric name.
  const yMeta = getFieldMeta(yChannel.ref, {
    format: yChannel.format,
    role: yColumn.descriptor?.role,
    currency: yColumn.descriptor?.currency,
    ctx
  });
  const yFormat = yMeta.format;

  const rowIndexByX = new Map<string, number>();
  const rows: Record<string, CellValue>[] = [];
  const seriesKeys: string[] = [];
  const totals = new Map<string, number>();

  xColumn.values.forEach((x, i) => {
    const rowKey = String(x);
    let rowIdx = rowIndexByX.get(rowKey);
    if (rowIdx === undefined) {
      rowIdx = rows.length;
      rowIndexByX.set(rowKey, rowIdx);
      rows.push({ [encoding.x.ref]: x });
    }
    const colorVal = colorColumn.values[i];
    const seriesKey =
      colorVal === null || colorVal === undefined ? NULL_SERIES_LABEL : String(colorVal);
    if (!totals.has(seriesKey)) {
      totals.set(seriesKey, 0);
      seriesKeys.push(seriesKey);
    }
    const y = yColumn.values[i];
    rows[rowIdx][seriesKey] = y;
    if (typeof y === 'number') {
      totals.set(seriesKey, (totals.get(seriesKey) ?? 0) + y);
    }
  });

  // Cap series to the top MAX_SERIES by total — a high-cardinality breakdown would otherwise
  // materialize one <Line> per distinct value and OOM the tab. The rest are summarized in `notice`.
  const orderedKeys = [...seriesKeys].sort((a, b) => (totals.get(b) ?? 0) - (totals.get(a) ?? 0));
  const keptKeys = orderedKeys.slice(0, MAX_SERIES);
  const series: PreparedSeries[] = keptKeys.map(key => ({
    key,
    label: key,
    metricLabel: yMeta.label,
    format: yChannel.format ?? yMeta.valueFormat,
    formatValue: yFormat,
    mark: yChannel.mark,
    dashed: yChannel.dashed,
    side: yChannel.side
  }));

  const xIsDate = xColumn.descriptor?.dataType === 'date';
  const sortedRows = xIsDate ? sortRowsByDateKey(rows, encoding.x.ref) : rows;
  // Prune each row to the kept keys (gap-filling absent ones with null so connectNulls:false breaks
  // render) and cap the point count in one pass.
  const cappedRows = sortedRows.slice(0, MAX_POINTS).map(row => {
    const next: Record<string, CellValue> = { [encoding.x.ref]: row[encoding.x.ref] };
    keptKeys.forEach(key => {
      next[key] = key in row ? row[key] : null;
    });
    return next;
  });

  return {
    rows: cappedRows,
    xKey: encoding.x.ref,
    xFormat: encoding.x.format,
    xIsDate,
    series,
    colorSplit: true,
    notice: buildNotice(orderedKeys.length - keptKeys.length, sortedRows.length - cappedRows.length)
  };
};

// Add a combo chart's overlay series to a color split: every `y` channel after the first whose
// effective mark differs from the split's (e.g. revenue lines over spend bars split by channel). The
// split fans `y[0]` alone, so without this they'd be dropped. An overlay is fetched by its own query,
// grouped by x only (bpm-types rejects anything else), so its rows don't line up with the split's
// long rows by index — each value is joined onto the split's row for the same x VALUE, and an x the
// split never saw gets a row of its own. Same-mark channels are left out, as before (a metric picker's
// siblings), so a non-combo color split is unchanged.
const withOverlaySeries = (
  split: PreparedGraphicChart,
  data: QueryResponse,
  encoding: GraphicEncoding,
  baseMark: SeriesMark,
  xColumn: ResolvedColumn,
  ctx?: FieldMetaCtx
): PreparedGraphicChart => {
  const splitMark = encoding.y[0]?.mark ?? baseMark;
  const channels = encoding.y.slice(1).filter(c => (c.mark ?? baseMark) !== splitMark);
  if (channels.length === 0) return split;

  const xColId = parseOutputFieldRef(encoding.x.ref).colId;
  const rows = split.rows.map(row => ({ ...row }));
  const rowByX = new Map(rows.map(row => [String(row[split.xKey]), row]));
  const overlays: PreparedSeries[] = [];

  channels.forEach(channel => {
    const column = resolveColumn(data, channel.ref);
    if (!column) return;
    // The overlay's own x column when its query came back as a separate block; the chart's x when it
    // shares the split's block.
    const ownX = resolveColumn(data, `${parseOutputFieldRef(channel.ref).queryId}.${xColId}`);
    const overlayX = ownX?.values.length === column.values.length ? ownX : xColumn;
    if (overlayX.values.length !== column.values.length) return;

    overlayX.values.forEach((x, i) => {
      const key = String(x);
      let row = rowByX.get(key);
      if (!row) {
        row = { [split.xKey]: x };
        rows.push(row);
        rowByX.set(key, row);
      }
      row[channel.ref] ??= column.values[i] ?? null;
    });

    const meta = getFieldMeta(channel.ref, {
      format: channel.format,
      label: channel.label,
      role: column.descriptor?.role,
      currency: column.descriptor?.currency,
      ctx
    });
    overlays.push({
      key: channel.ref,
      label: meta.label,
      metricLabel: meta.label,
      color: channel.color,
      format: channel.format ?? meta.valueFormat,
      formatValue: meta.format,
      mark: channel.mark,
      dashed: channel.dashed,
      side: channel.side,
      overlay: true
    });
  });
  if (overlays.length === 0) return split;

  // Gap-fill with null (not undefined) so connectNulls:false breaks lines and bars stay absent.
  const empty = Object.fromEntries([...split.series, ...overlays].map(s => [s.key, null]));
  const filled = rows.map(row => ({ ...empty, ...row }));
  return {
    ...split,
    rows: split.xIsDate ? sortRowsByDateKey(filled, split.xKey) : filled,
    series: [...split.series, ...overlays]
  };
};

// One resolved scatter cloud: the point rows for a single query's (x, y[, label]) columns, written
// under the given output keys, plus whether a label column resolved. Rows missing x or y are dropped
// (a point needs both coordinates). Returns null when x or y can't be resolved.
interface ScatterCloud {
  points: Record<string, CellValue>[];
  hasLabel: boolean;
}

const buildScatterCloud = (
  data: QueryResponse,
  xRef: string,
  yRef: string,
  labelRef: string | null,
  xKey: string,
  yKey: string
): ScatterCloud | null => {
  const xColumn = resolveColumn(data, xRef);
  const yColumn = resolveColumn(data, yRef);
  if (!xColumn || !yColumn) return null;
  const labelColumn = labelRef ? resolveColumn(data, labelRef) : null;
  const points: Record<string, CellValue>[] = [];
  xColumn.values.forEach((x, i) => {
    const y = yColumn.values[i];
    if (x === null || x === undefined || y === null || y === undefined) return;
    const row: Record<string, CellValue> = { [xKey]: x, [yKey]: y };
    if (labelColumn) {
      row[POINT_LABEL_KEY] = labelColumn.values[i] ?? null;
    }
    points.push(row);
  });
  return { points, hasLabel: labelColumn !== null };
};

// Re-qualify an OutputFieldRef from one query to another: "q0.spend" -> "q1.spend". Derives
// the prior period's columns from the declared base refs for a point comparison (the encoding names
// only the base query; the prior cloud is read from `comparison.prior`'s block).
const swapQueryId = (ref: string, from: string, to: string): string =>
  ref.startsWith(`${from}.`) ? `${to}.${ref.slice(from.length + 1)}` : ref;

// A scatter (point chart) is a cloud of (x, y) points — NOT a pivot into a series per color value the
// way line/bar/area fan a measure across a dimension. x and y come from the encoding; an optional
// color dimension (e.g. campaign) rides along as a per-point LABEL for the tooltip rather than fanning
// into one illegible series per value.
//
// Period-over-period comparison is the point-type REALIZATION of the declared `comparison` intent: two
// clouds (base vs prior), colored by period, sharing one numeric x/y axis. The two queries stay
// SEPARATE blocks (no merge) — the prior cloud's refs are derived from the base refs by swapping the
// query prefix, and each cloud becomes its own <Scatter> via `series.points`. When the intent isn't
// declared (or the prior columns don't resolve) it falls back to the single-cloud shape.
const prepareScatter = (
  config: ChartConfig,
  encoding: GraphicEncoding,
  data: QueryResponse,
  ctx?: FieldMetaCtx
): PreparedGraphicChart | null => {
  const yChannel = encoding.y[0];
  if (!yChannel) return null;
  // A scatter's x can be a dimension or a metric (MARK_RULES.point.x === "any", e.g. a date x-axis),
  // so its role must come from the resolved column rather than the registry-lookup default.
  const xColumn = resolveColumn(data, encoding.x.ref);
  const yColumn = resolveColumn(data, yChannel.ref);
  const yMeta = getFieldMeta(yChannel.ref, {
    format: yChannel.format,
    label: yChannel.label,
    role: yColumn?.descriptor?.role,
    currency: yColumn?.descriptor?.currency,
    ctx
  });
  // A scatter's x can be a metric, so it carries a currency of its own — pass it rather than letting
  // a native-money x fall back to the company default the way the y axis would have.
  const xMeta = getFieldMeta(encoding.x.ref, {
    format: encoding.x.format,
    label: encoding.x.label,
    role: xColumn?.descriptor?.role,
    currency: xColumn?.descriptor?.currency,
    ctx
  });
  const axes = {
    xFormat: encoding.x.format ?? xMeta.valueFormat,
    xLabel: xMeta.label,
    xFormatValue: xMeta.format,
    xIsDate: false,
    xIsNumeric: true
  } as const;

  const comparison = resolveComparison(config);
  const baseQueryId = parseOutputFieldRef(encoding.x.ref).queryId;
  // Point comparison: two clouds sharing one numeric x/y axis. Derive the prior cloud's refs from the
  // base refs (encoding names only the base query) and render each cloud as its own <Scatter>. Only
  // when the intent is declared over the query encoding.x points at AND both clouds resolve — else
  // fall through to the single-cloud path (degrade, don't crash).
  if (
    comparison &&
    baseQueryId &&
    comparison.base === baseQueryId &&
    comparison.prior !== baseQueryId
  ) {
    const colorRef = encoding.color?.ref ?? null;
    const toPrior = (ref: string): string => swapQueryId(ref, baseQueryId, comparison.prior);
    const baseCloud = buildScatterCloud(
      data,
      encoding.x.ref,
      yChannel.ref,
      colorRef,
      SCATTER_X_KEY,
      SCATTER_Y_KEY
    );
    const priorCloud = buildScatterCloud(
      data,
      toPrior(encoding.x.ref),
      toPrior(yChannel.ref),
      colorRef ? toPrior(colorRef) : null,
      SCATTER_X_KEY,
      SCATTER_Y_KEY
    );
    if (baseCloud && priorCloud) {
      const basePoints = baseCloud.points.slice(0, MAX_POINTS);
      const priorPoints = priorCloud.points.slice(0, MAX_POINTS);
      const cloudSeries = (points: Record<string, CellValue>[], group: string): PreparedSeries => ({
        key: SCATTER_Y_KEY,
        label: yMeta.label,
        metricLabel: yMeta.label,
        pointGroupLabel: group,
        format: yChannel.format ?? yMeta.valueFormat,
        formatValue: yMeta.format,
        mark: 'point',
        points
      });
      const overflow =
        Math.max(0, baseCloud.points.length - MAX_POINTS) +
        Math.max(0, priorCloud.points.length - MAX_POINTS);
      return {
        ...axes,
        xKey: SCATTER_X_KEY,
        rows: [...basePoints, ...priorPoints],
        series: [cloudSeries(basePoints, 'This period'), cloudSeries(priorPoints, 'Prior period')],
        pointLabelKey: baseCloud.hasLabel ? POINT_LABEL_KEY : undefined,
        notice: buildNotice(0, overflow)
      };
    }
  }

  const cloud = buildScatterCloud(
    data,
    encoding.x.ref,
    yChannel.ref,
    encoding.color?.ref ?? null,
    encoding.x.ref,
    yChannel.ref
  );
  if (!cloud) return null;
  const series: PreparedSeries[] = [
    {
      key: yChannel.ref,
      label: yMeta.label,
      metricLabel: yMeta.label,
      color: yChannel.color,
      format: yChannel.format ?? yMeta.valueFormat,
      formatValue: yMeta.format,
      mark: 'point',
      dashed: yChannel.dashed,
      side: yChannel.side
    }
  ];
  return {
    ...axes,
    xKey: encoding.x.ref,
    rows: cloud.points.slice(0, MAX_POINTS),
    series,
    pointLabelKey: cloud.hasLabel ? POINT_LABEL_KEY : undefined,
    notice: buildNotice(0, Math.max(0, cloud.points.length - MAX_POINTS))
  };
};

// Breakdown ∘ comparison realization: fan the base (current) and prior windows across the SAME
// breakdown dimension, pairing each value's two windows into a color-matched solid + dashed series.
// Reuses the single-window pivot (prepareColorSplit) once per window, then zips them by ordinal
// position (alignBy:"ordinal" — the two windows' dates don't coincide, so row i of current pairs with
// row i of prior). Returns null when either window can't be pivoted, so the caller degrades to a
// base-only split instead of crashing.
const prepareComparisonColorSplit = (
  data: QueryResponse,
  encoding: GraphicEncoding,
  xColumn: ResolvedColumn,
  colorRef: string,
  comparison: ComparisonRoles,
  ctx?: FieldMetaCtx
): PreparedGraphicChart | null => {
  const base = prepareColorSplit(data, encoding, xColumn, colorRef, ctx);
  if (!base) return null;

  // Prior window: re-qualify every base ref (x / color / metrics) onto the prior query — the encoding
  // names only the base. Rebuilding as `${prior}.${colId}` keeps the qualified OutputFieldRef shape
  // (string parts satisfy the branded type, no cast). The prior x/color columns must resolve or there's
  // no pair.
  const toPrior = (ref: string): OutputFieldRef =>
    `${comparison.prior}.${parseOutputFieldRef(ref).colId}`;
  const priorEncoding: GraphicEncoding = {
    ...encoding,
    x: { ...encoding.x, ref: toPrior(encoding.x.ref) },
    y: encoding.y.map(channel => ({ ...channel, ref: toPrior(channel.ref) }))
  };
  const priorXColumn = resolveColumn(data, priorEncoding.x.ref);
  if (!priorXColumn) return null;
  const prior = prepareColorSplit(data, priorEncoding, priorXColumn, toPrior(colorRef), ctx);
  if (!prior) return null;

  // Breakdown values kept + ordered by the base window (largest first, already capped to MAX_SERIES);
  // each becomes a current/prior pair sharing one palette color, prior dashed.
  const values = base.series.map(s => s.key);
  const priorKey = (value: string): string => `${value}${PRIOR_KEY_SUFFIX}`;
  const series: PreparedSeries[] = values.flatMap((value, idx) => {
    const color = SERIES_PALETTE[idx % SERIES_PALETTE.length];
    const from = base.series[idx];
    const shared = {
      color,
      metricLabel: from.metricLabel,
      format: from.format,
      formatValue: from.formatValue
    };
    return [
      { ...shared, key: value, label: withPeriod(value, 'current'), period: 'current' as const },
      {
        ...shared,
        key: priorKey(value),
        label: withPeriod(value, 'prior'),
        period: 'prior' as const,
        dashed: true
      }
    ];
  });

  // Zip by ordinal position: row i carries the i-th base date's x plus every value's current (from
  // base row i) and prior (from prior row i) cell. Unmatched tail positions stay null so
  // connectNulls:false breaks the line — matching the "shorter window → trailing nulls" semantics.
  const rowCount = Math.max(base.rows.length, prior.rows.length);
  const rows: Record<string, CellValue>[] = [];
  for (let i = 0; i < rowCount; i += 1) {
    const baseRow = base.rows[i];
    const priorRow = prior.rows[i];
    const row: Record<string, CellValue> = {
      [encoding.x.ref]: baseRow ? baseRow[base.xKey] : (priorRow?.[prior.xKey] ?? null)
    };
    values.forEach(value => {
      row[value] = baseRow ? (baseRow[value] ?? null) : null;
      row[priorKey(value)] = priorRow ? (priorRow[value] ?? null) : null;
    });
    rows.push(row);
  }

  return {
    rows,
    xKey: encoding.x.ref,
    xFormat: encoding.x.format,
    xIsDate: base.xIsDate,
    series,
    // Every pair plots the same metric; name it once for the tooltip header (rows carry value·period).
    comparisonMetricLabel: getFieldMeta(encoding.y[0].ref, { ctx }).label,
    notice: base.notice
  };
};

// Map raw columnar cells to the [{ xRef: x, yRef: y, ... }] rows recharts consumes. Returns null
// for non-cartesian (table/stat/pie) configs or when an encoding ref can't be resolved.
export const prepareGraphicChart = (
  config: ChartConfig,
  data: QueryResponse,
  ctx?: FieldMetaCtx,
  // Live breakdown selection from the picker (React view state). Wins over the declared breakdown
  // control / encoding.color when its dimension is present in the payload; ignored otherwise.
  breakdownRef?: string
): PreparedGraphicChart | null => {
  if (config.type === 'table' || config.type === 'stat' || config.type === 'pie') return null;
  const prepared = prepareCartesianRows(config, data, ctx, breakdownRef);
  if (!prepared || prepared.xLabel !== undefined) return prepared;
  // Name the x axis after its dimension for the axis title. Scatter sets its own (its x is a metric);
  // every other prep path leaves it for here. getFieldMeta labels a dimension with its bare id, so
  // titleize it ("platform" → "Platform") the same way the breakdown picker names its options.
  const { colId } = parseOutputFieldRef(config.encoding.x.ref);
  return { ...prepared, xLabel: config.encoding.x.label ?? titleize(colId) };
};

const prepareCartesianRows = (
  config: ChartConfig,
  data: QueryResponse,
  ctx?: FieldMetaCtx,
  breakdownRef?: string
): PreparedGraphicChart | null => {
  if (config.type === 'table' || config.type === 'stat' || config.type === 'pie') return null;

  const { encoding } = config;
  const xColumn = resolveColumn(data, encoding.x.ref);
  if (!xColumn) return null;

  // Scatter is points over a continuous x, not a pivot — handle it before the color-split fan-out.
  if (config.type === 'point') return prepareScatter(config, encoding, data, ctx);

  // Comparison is a DECLARED intent (resolveComparison — the Phase 3 B1 role-map), never re-inferred.
  const comparison = resolveComparison(config);
  const isComparison = Boolean(comparison);

  // A color split fans one metric into a series per distinct dimension value. Precedence: a live
  // picker selection, then a declared breakdown control, then encoding.color (no leftover-dim guess —
  // Round B deleted it). The request is grouped by exactly (x, active breakdown) — see
  // withActiveBreakdown — so the pivot is 1:1.
  const colorRef = resolveColorRef(config, data, breakdownRef);
  if (colorRef) {
    // Breakdown ∘ comparison compose: fan BOTH windows across the breakdown so every value gets a
    // solid current line and a color-matched dashed prior line. This realization lives at the Phase-2
    // (breakdown) × Phase-3 (comparison) intersection the two phases never wired together.
    if (comparison) {
      const combo = prepareComparisonColorSplit(data, encoding, xColumn, colorRef, comparison, ctx);
      if (combo) return combo;
    }
    const split = prepareColorSplit(data, encoding, xColumn, colorRef, ctx);
    if (split) return withOverlaySeries(split, data, encoding, config.type, xColumn, ctx);
  }

  const series: PreparedSeries[] = [];
  const seriesValues: CellValue[][] = [];
  for (const channel of encoding.y) {
    const column = resolveColumn(data, channel.ref);
    if (!column) continue;
    const meta = getFieldMeta(channel.ref, {
      format: channel.format,
      label: channel.label,
      role: column.descriptor?.role,
      currency: column.descriptor?.currency,
      ctx
    });
    // On a comparison chart, tag the series by its window and suffix the label so the legend/tooltip
    // read "Spend · current" / "Spend · prior". Derived from the query prefix, so it works with OR
    // without a date dimension (a category-x comparison has no date column to derive a range from,
    // which used to collapse both windows to a bare "Spend").
    const period = getComparisonPeriod(channel.ref, comparison);
    series.push({
      key: channel.ref,
      label: period ? withPeriod(meta.label, period) : meta.label,
      metricLabel: meta.label,
      color: channel.color,
      format: channel.format ?? meta.valueFormat,
      formatValue: meta.format,
      mark: channel.mark,
      dashed: channel.dashed,
      side: channel.side,
      period
    });
    seriesValues.push(column.values);
  }
  if (series.length === 0) return null;

  // When a comparison overlay's series all plot the SAME metric, surface that metric's name (from the
  // registry, NOT the period-suffixed series labels) so the tooltip can show it once as a header.
  let comparisonMetricLabel: string | undefined;
  if (isComparison && new Set(series.map(s => parseOutputFieldRef(s.key).colId)).size === 1) {
    comparisonMetricLabel = getFieldMeta(series[0].key, { ctx }).label;
  }

  // The ordinal merge keeps each query's own date, row-aligned with the base's.
  const priorXColumn = comparison
    ? resolveColumn(data, `${comparison.prior}.${parseOutputFieldRef(encoding.x.ref).colId}`)
    : undefined;

  const rows: Record<string, CellValue>[] = xColumn.values.map((x, i) => {
    const row: Record<string, CellValue> = { [encoding.x.ref]: x };
    series.forEach((s, si) => {
      row[s.key] = seriesValues[si][i] ?? null;
    });
    if (priorXColumn) {
      row[PRIOR_X_KEY] = priorXColumn.values[i] ?? null;
    }
    return row;
  });

  const xIsDate = xColumn.descriptor?.dataType === 'date';
  const sortedRows = xIsDate ? sortRowsByDateKey(rows, encoding.x.ref) : rows;
  // Cap points — a non-date categorical x (e.g. thousands of campaigns) would otherwise render a
  // bar/point per row and OOM the tab.
  const cappedRows = sortedRows.slice(0, MAX_POINTS);
  return {
    rows: cappedRows,
    xKey: encoding.x.ref,
    xFormat: encoding.x.format,
    xIsDate,
    series,
    comparisonMetricLabel,
    priorXKey: priorXColumn ? PRIOR_X_KEY : undefined,
    notice: buildNotice(0, sortedRows.length - cappedRows.length)
  };
};

// One donut slice. `share` is value / total, in [0, 1].
export interface PreparedPieSlice {
  key: string;
  label: string;
  value: number;
  share: number;
  color: string;
  isOther: boolean;
}

// A pie chart flattened from the columnar QueryResponse into ordered slices.
export interface PreparedPie {
  slices: PreparedPieSlice[];
  total: number;
  metricLabel: string;
  // Registry-aware value formatter (currency/metric-correct), bound at prep via getFieldMeta.
  formatValue: (value: CellValue) => string;
  notice?: string;
}

export type PreparePieResult = { ok: true; pie: PreparedPie } | { ok: false; message: string };

// Named slices before the tail folds into "Other". Five + Other = six, which fits SERIES_PALETTE.
export const PIE_TOP_SLICES = 5;
const PIE_OTHER_KEY = '__other';

const UNMAPPABLE = "Couldn't map this chart's data to its encoding.";

const pieSliceLabel = (x: CellValue | undefined, format?: ValueFormat): string => {
  if (x === null || x === undefined) return NULL_SERIES_LABEL;
  if (typeof x === 'number') return formatChartValue(x, format);
  return String(x);
};

// Turn a pie config + response into ordered donut slices. Rows are SUMMED per x value, so a query
// that groups by more dimensions than x collapses correctly. That is safe only because the metric is
// additive: bpm-types rejects ratios (PIE_METRIC_NOT_ADDITIVE), and this re-checks in case a stored
// config skipped validation. The tail beyond PIE_TOP_SLICES folds into one "Other" slice. That is
// correct because bpm-types also rejects rowLimit/page on a pie's query (TRUNCATED_QUERY_NOT_ALLOWED),
// so the payload holds every row.
export const preparePie = (
  config: ChartConfig,
  data: QueryResponse,
  ctx?: FieldMetaCtx
): PreparePieResult => {
  if (config.type !== 'pie') return { ok: false, message: UNMAPPABLE };
  const { encoding } = config;
  const yChannel = encoding.y[0];
  const xColumn = resolveColumn(data, encoding.x.ref);
  const yColumn = yChannel ? resolveColumn(data, yChannel.ref) : null;
  if (!yChannel || !xColumn || !yColumn) return { ok: false, message: UNMAPPABLE };

  if (requiresCustomAggregation(metricName(yChannel.ref)) || isCustomMetricRef(yChannel.ref, ctx)) {
    return {
      ok: false,
      message: 'Pie charts need a metric that adds up across slices, like spend or impressions.'
    };
  }

  const sums = new Map<string, number>();
  xColumn.values.forEach((x, i) => {
    const label = pieSliceLabel(x, encoding.x.format);
    const y = yColumn.values[i];
    sums.set(label, (sums.get(label) ?? 0) + (typeof y === 'number' ? y : 0));
  });

  const positive = [...sums.entries()].filter(([, value]) => value > 0);
  const labelsWhere = (keep: (value: number) => boolean): string[] =>
    [...sums.entries()]
      .filter(([, value]) => keep(value))
      .map(([label]) => label)
      .sort((a, b) => a.localeCompare(b));
  const zero = labelsWhere(value => value === 0);
  const negative = labelsWhere(value => value < 0);
  if (positive.length === 0) return { ok: false, message: 'No data to show for this period.' };

  positive.sort(([la, va], [lb, vb]) => vb - va || la.localeCompare(lb));
  const total = positive.reduce((sum, [, value]) => sum + value, 0);

  const named = positive.length > PIE_TOP_SLICES + 1 ? positive.slice(0, PIE_TOP_SLICES) : positive;
  const tail = positive.slice(named.length);
  const slices: PreparedPieSlice[] = named.map(([label, value], i) => ({
    key: label,
    label,
    value,
    share: value / total,
    color: SERIES_PALETTE[i % SERIES_PALETTE.length],
    isOther: false
  }));
  if (tail.length > 0) {
    const value = tail.reduce((sum, [, v]) => sum + v, 0);
    slices.push({
      key: PIE_OTHER_KEY,
      label: `Other (${tail.length})`,
      value,
      share: value / total,
      color: Neutral400,
      isOther: true
    });
  }

  const meta = getFieldMeta(yChannel.ref, {
    format: yChannel.format,
    label: yChannel.label,
    role: yColumn.descriptor?.role,
    currency: yColumn.descriptor?.currency,
    ctx
  });
  return {
    ok: true,
    pie: {
      slices,
      total,
      metricLabel: meta.label,
      formatValue: meta.format,
      notice: droppedSlicesNotice(zero, negative, meta.label)
    }
  };
};

// Is this metric ref one of the company's custom metrics? Same lookup bpm-types' getFieldMeta uses:
// the query's channel (ctx.channelByQueryId), then that channel's custom metrics by column id.
const isCustomMetricRef = (ref: string, ctx?: FieldMetaCtx): boolean => {
  const { queryId, colId } = parseOutputFieldRef(ref);
  const channel = ctx?.channelByQueryId?.[queryId];
  return Boolean(channel && ctx?.customMetrics?.[channel]?.[colId]);
};

// Most category names each "not shown" list names before summarising the rest as a count.
const PIE_NOTICE_MAX_NAMES = 5;

const notShownList = (reason: string, labels: string[]): string | undefined => {
  if (labels.length === 0) return undefined;
  const named = labels.slice(0, PIE_NOTICE_MAX_NAMES).join(', ');
  const rest = labels.length - PIE_NOTICE_MAX_NAMES;
  return `Not shown (${reason}): ${named}${rest > 0 ? ` and ${rest} more` : ''}`;
};

// Names the categories a pie dropped, split by why: a zero total ("no spend") or a negative one
// ("negative spend", e.g. revenue net of returns). Undefined when nothing was dropped.
const droppedSlicesNotice = (
  zero: string[],
  negative: string[],
  metricLabel: string
): string | undefined => {
  const metric = metricLabel.toLowerCase();
  const parts = [
    notShownList(`no ${metric}`, zero),
    notShownList(`negative ${metric}`, negative)
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
};

// One header column of a rendered table.
export interface PreparedTableColumn {
  key: string;
  label: string;
  // Registry-aware value formatter, bound at prep via getFieldMeta.
  formatValue: (value: CellValue) => string;
  isDate: boolean;
  isMetric: boolean;
  // Opt-OUT: sortable unless the config explicitly disables it. TableColumn.sortable is declared in
  // bpm-types but not yet emitted by config generation, so opt-in would ship an inert feature.
  // Tighten to `=== true` once configs reliably declare it.
  sortable: boolean;
  /**
   * Bound per-cell heat style, or absent when this column renders plain. Returns `null` for a cell
   * with no number to shade. Bound at prep -- like `formatValue` -- so the renderer holds no
   * heat-map rules and the domain is computed once per column rather than once per cell.
   */
  heatStyle?: (value: CellValue) => HeatStyle | null;
  /**
   * Resolved header description + formula, or absent when nothing resolves. Bound at prep -- like
   * `formatValue` and `heatStyle` -- so the renderer holds no glossary rules and the resolution
   * order lives in one testable place.
   */
  description?: ColumnDescription;
  // Superheader label from the config's `TableColumn.group`; absent = ungrouped.
  group?: string;
}

// One cell of a table's superheader row: a run of adjacent columns sharing a `group` label.
export interface PreparedColumnGroup {
  // The run's first column key — unique, so it doubles as the React key.
  key: string;
  // null = a blank cell over ungrouped columns.
  label: string | null;
  span: number;
}

// Merge adjacent columns with equal `group` labels into superheader runs. Undefined when no column is
// grouped, so an ungrouped table renders no superheader row at all. Validation already rejects blank
// and non-contiguous labels; here a blank label is just ungrouped and a repeated label starts a new
// run, so a config that skipped validation still renders aligned.
export const buildColumnGroups = (
  columns: PreparedTableColumn[]
): PreparedColumnGroup[] | undefined => {
  // Trimmed, matching validation; a non-string from an unvalidated saved config is ungrouped.
  const labelOf = ({ group }: PreparedTableColumn): string | null =>
    typeof group === 'string' && group.trim() ? group.trim() : null;
  if (!columns.some(col => labelOf(col) !== null)) return undefined;
  const groups: PreparedColumnGroup[] = [];
  columns.forEach(col => {
    const label = labelOf(col);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.span += 1;
    } else {
      groups.push({ key: col.key, label, span: 1 });
    }
  });
  return groups;
};

// A table's grand-total footer, sourced from a sibling 0-dimension totals query (never client
// summing) — so ratio totals are SQL-correct (sum(num)/sum(den)) and stay correct even when rowLimit
// truncates the shown rows (the totals query aggregates the FULL dataset server-side).
export interface PreparedTableFooter {
  // Cell value per column key (SAME keys as PreparedTable.columns[].key). A missing key = blank cell.
  cells: Record<string, CellValue>;
  // The column key that shows the "Total" text label instead of a value (the first dimension column).
  labelKey?: string;
  label: string; // "Total"
}

export interface PreparedTable {
  columns: PreparedTableColumn[];
  rows: Record<string, CellValue>[];
  // Rows dropped by the encoding's rowLimit, surfaced so the UI can note the cap.
  truncated: number;
  // Superheader row, present only when at least one column has a `group`. Built from the RESOLVED
  // columns, so a dropped column shrinks its run rather than misaligning the header.
  columnGroups?: PreparedColumnGroup[];
  // Grand-total footer, present only when the encoding opts in (showGrandTotal + totalsQueryId(s)) and
  // at least one metric column resolves a value from the totals block. Independent of rowLimit.
  footer?: PreparedTableFooter;
  // Build that same footer from a DIFFERENT totals payload — the search-scoped re-run of the
  // 0-dimension totals query. Same column rules, different numbers.
  buildFooterFrom: (totals: QueryResponse) => PreparedTableFooter | undefined;
}

// Compute a delta per the StatCompare/DerivedColumn op. percentDelta = (cur - prev) / prev;
// absoluteDelta = cur - prev; ratio = cur / prev. Non-numeric or zero-denominator cases => null.
// Shared by the stat compare badge and the table derived column (one delta vocabulary).
const computeStatDelta = (
  current: CellValue,
  previous: CellValue,
  mode: StatCompareMode
): number | null => {
  if (typeof current !== 'number' || typeof previous !== 'number') return null;
  switch (mode) {
    case 'absoluteDelta':
      return current - previous;
    case 'ratio':
      return previous === 0 ? null : current / previous;
    default:
      return previous === 0 ? null : (current - previous) / previous;
  }
};

// Fallback header for a derived column when the config omits `label`.
const DERIVED_COLUMN_LABEL: Record<StatCompareMode, string> = {
  percentDelta: '% Δ',
  absoluteDelta: 'Δ',
  ratio: 'Ratio'
};

// Default format per derived op when the config doesn't override it: %Δ renders as a percent, a
// ratio is dimensionless (a plain number). absoluteDelta is omitted so it keeps the base measure's
// own formatter (e.g. a currency delta stays currency).
const DERIVED_COLUMN_FORMAT: Partial<Record<StatCompareMode, ValueFormat>> = {
  percentDelta: { kind: 'percent' },
  ratio: { kind: 'number' }
};

// One fully-resolved table column: its render descriptor, its column values, and the raw TableColumn
// (kept so the footer can read the `derived` / `aggregate` hints and re-qualify `ref` to the totals
// query).
interface ResolvedTableColumn {
  def: PreparedTableColumn;
  values: CellValue[];
  col: TableColumn;
}

// Build the grand-total footer from the sibling 0-dimension totals queries. The VALUE always comes from
// that query (SQL-correct incl. ratios, truncation-safe); the per-column `aggregate` hint only decides
// WHICH columns show a footer cell. Returns undefined when the footer isn't opted in, when the totals
// block/row is absent (degrade — the table still renders its rows), when no metric column resolves
// a value (nothing to show), or when the rows are metric-filtered and the totals query can't be
// derived from the detail query (see `totalsIgnoresMetricFilters`). Otherwise the totals query is
// given the detail query's metric filters, tagged with a rollup grain, at fetch time
// (`prepareChartRequest` in `useChartData`), so its total covers the same filtered population.
const buildTableFooter = (
  resolved: ResolvedTableColumn[],
  encoding: TableEncoding,
  data: QueryResponse,
  queries: QuerySpec[],
  merge: QueryConfig['merge']
): PreparedTableFooter | undefined => {
  const { showGrandTotal, totalsQueryId, totalsQueryIds } = encoding;
  if (!showGrandTotal || (!totalsQueryId && !totalsQueryIds)) return undefined;
  // Derivation couldn't scope the totals query to the filtered rows, so any footer here would total
  // a wider population than the rows above it.
  if (totalsIgnoresMetricFilters(queries, encoding, merge)) return undefined;

  const cells: Record<string, CellValue> = {};
  let labelKey: string | undefined;
  let hasMetricValue = false;

  resolved.forEach(({ def, col }) => {
    // Derived columns (e.g. per-row %Δ): BLANK — a per-row ratio isn't a summable total.
    if (col.derived) return;
    // Dimension column: no footer value; the first one shows the "Total" label.
    if (!def.isMetric) {
      if (!labelKey) {
        labelKey = def.key;
      }
      return;
    }
    // Metric column: show the aggregated value unless the column opts out via the `none` hint.
    if (col.aggregate === 'none' || !col.ref) {
      return;
    }

    const { queryId, colId } = parseOutputFieldRef(col.ref);
    const columnTotalsId = totalsQueryIds ? totalsQueryIds[queryId] : totalsQueryId;
    if (!columnTotalsId) return;

    const totalsValue = resolveColumn(data, `${columnTotalsId}.${colId}`)?.values[0];
    if (totalsValue === undefined) {
      return;
    }
    cells[def.key] = totalsValue;
    hasMetricValue = true;
  });

  if (!hasMetricValue) return undefined;
  return { cells, labelKey, label: 'Total' };
};

export const UNMERGED_TABLE_MESSAGE =
  "This table's queries didn't merge, so its rows can't be lined up and aren't shown.";

// A table zips its columns by row index, which lines rows up only within one response block. Columns
// from separate blocks (a merge that didn't plan) would pair unrelated rows.
export const tableSpansBlocks = (config: ChartConfig, data: QueryResponse): boolean => {
  if (config.type !== 'table') return false;
  const refs = config.encoding.columns.flatMap(col => {
    if (col.derived) return [col.derived.of, col.derived.vs];
    return col.ref ? [col.ref] : [];
  });
  const blockIds = new Set(refs.map(ref => resolveColumn(data, ref)?.blockId).filter(Boolean));
  return blockIds.size > 1;
};

// Flatten a table ChartConfig + its columnar response into header columns + row objects: resolve
// each column (a produced ref, or a derived this/prior column computed per-row from `of`/`vs`), zip
// the parallel arrays into rows, apply rowLimit. Pivot / pinned columns are not handled yet (POC).
// Returns null for non-table configs, unresolvable refs, or columns from separate blocks
// (tableSpansBlocks).
export const prepareTable = (
  config: ChartConfig,
  data: QueryResponse,
  ctx?: FieldMetaCtx,
  sort?: ColumnSort
): PreparedTable | null => {
  if (config.type !== 'table' || tableSpansBlocks(config, data)) return null;
  const { encoding } = config;

  const resolved = encoding.columns
    .map(col => {
      // Derived column (e.g. this/prior %Δ): computed per-row from `of`/`vs` rather than read from a
      // produced column. Safe for any metric — a per-row delta of two already-aggregated scalars
      // never re-aggregates (see DerivedColumn in bpm-types). An explicit format wins; else percent
      // for %Δ, and the base measure's own formatter for an absolute delta / ratio.
      if (col.derived) {
        const { op, of, vs } = col.derived;
        const ofColumn = resolveColumn(data, of);
        const vsColumn = resolveColumn(data, vs);
        if (!ofColumn || !vsColumn) return null;
        const values: CellValue[] = ofColumn.values.map((v, i) =>
          computeStatDelta(v, vsColumn.values[i] ?? null, op)
        );
        const format: ValueFormat | undefined = col.format ?? DERIVED_COLUMN_FORMAT[op];
        const def: PreparedTableColumn = {
          key: `${of}→${vs}:${op}`,
          label: col.label ?? DERIVED_COLUMN_LABEL[op],
          // An absolute delta keeps the base measure's own formatter (DERIVED_COLUMN_FORMAT omits it),
          // so it needs `of`'s currency too — a %Δ / ratio overrides the kind and ignores it.
          formatValue: getFieldMeta(of, { format, currency: ofColumn.descriptor?.currency, ctx })
            .format,
          isDate: false,
          isMetric: true,
          sortable: col.sortable !== false,
          group: col.group,
          // A derived column has no `ref` and so no registry entry: it renders plain unless the
          // config authors `heatMap` explicitly.
          heatStyle: buildColumnHeatStyle({ col, values, isMetric: true, isDate: false }),
          // A derived column has no registry identity, so there is no default to resolve -- only an
          // authored description renders.
          description: resolveColumnDescription({ description: col.description })
        };
        return { def, values, col };
      }
      if (!col.ref) return null;
      const column = resolveColumn(data, col.ref);
      if (!column) return null;
      const meta = getFieldMeta(col.ref, {
        format: col.format,
        label: col.label,
        role: column.descriptor?.role,
        currency: column.descriptor?.currency,
        ctx
      });
      const isMetric = column.descriptor?.role === 'metric';
      const isDate = column.descriptor?.dataType === 'date';
      // The registry declares a scheme PER CHANNEL, so the lookup needs the channel this column's
      // query reads. `channelByQueryId` is already built by useFieldMetaCtx for getFieldMeta.
      const { queryId } = parseOutputFieldRef(col.ref);
      const def: PreparedTableColumn = {
        key: col.ref,
        label: meta.label,
        formatValue: meta.format,
        isDate,
        isMetric,
        sortable: col.sortable !== false,
        group: col.group,
        heatStyle: buildColumnHeatStyle({
          col,
          values: column.values,
          isMetric,
          isDate,
          channel: ctx?.channelByQueryId?.[queryId]
        }),
        description: resolveColumnDescription({
          ref: col.ref,
          description: col.description,
          ctx,
          isMetric
        })
      };
      return { def, values: column.values, col };
    })
    .filter((c): c is ResolvedTableColumn => c !== null);
  if (resolved.length === 0) return null;

  const rowCount = Math.max(...resolved.map(r => r.values.length));
  const allRows: Record<string, CellValue>[] = [];
  for (let i = 0; i < rowCount; i += 1) {
    const row: Record<string, CellValue> = {};
    resolved.forEach(r => {
      row[r.def.key] = r.values[i] ?? null;
    });
    allRows.push(row);
  }
  // Sort by the date column (if any) ascending BEFORE truncating, so a rowLimit keeps a contiguous
  // earliest-first window rather than an arbitrary unsorted slice. A user's click-to-sort selection
  // replaces that default — and MUST also be applied here, upstream of the slice below, or it would
  // reorder an arbitrary window while presenting it as top-N (APP-GUIDE, "If click-to-sort is your
  // ticket"). If server-side pagination or a real SQL LIMIT ever lands, this becomes wrong: the
  // client would only ever be sorting the page it holds.
  const columns = resolved.map(r => r.def);
  const dateCol = resolved.find(r => r.def.isDate);
  let sortedRows = allRows;
  if (sort) {
    sortedRows = sortRows(allRows, columns, sort);
  } else if (dateCol) {
    sortedRows = sortRowsByDateKey(allRows, dateCol.def.key);
  }
  const shown = Math.min(rowCount, encoding.rowLimit ?? rowCount);
  const displayedRows = sortedRows.slice(0, shown);
  // The footer ALWAYS reflects the full dataset via its own 0-dimension totals query — never a client
  // aggregation, so ratio totals stay sum(num)/sum(den) rather than a mean of ratios, and a rowLimit
  // cannot make the total disagree with the data it summarizes.
  return {
    columns,
    rows: displayedRows,
    truncated: rowCount - shown,
    columnGroups: buildColumnGroups(columns),
    footer: buildTableFooter(resolved, encoding, data, config.queries, config.merge),
    // Re-derive the SAME footer from a different totals payload: the search-scoped re-run of the
    // 0-dimension query. Closing over `resolved` keeps one definition of "which columns get a total
    // cell, and where the Total label goes", so the searched footer cannot drift from the idle one.
    // Calling this does not recompute the row pipeline, which is what lets the footer's fetch state
    // stay out of this memo entirely.
    buildFooterFrom: (totals: QueryResponse) =>
      buildTableFooter(resolved, encoding, totals, config.queries, config.merge)
  };
};

// One sparkline point: a bucket value (x, typically a date string) and its measure (y).
export interface PreparedStatSparklinePoint {
  x: CellValue;
  y: CellValue;
}

// A stat (scorecard) ChartConfig flattened into the single value + optional compare delta +
// optional sparkline the StatRenderer draws.
export interface PreparedStat {
  value: CellValue;
  // Registry-aware formatter for the value (and sparkline), bound at prep via getFieldMeta.
  formatValue: (value: CellValue) => string;
  label?: string;
  compare?: {
    delta: number | null;
    // The compare ref's raw value, kept so the renderer can show "vs <prior>" if it wants.
    previous: CellValue;
    mode: StatCompareMode;
    // Whether the delta should read as "good" (drives up/down coloring). Undefined => neutral.
    isGood?: boolean;
    format?: ValueFormat;
  };
  sparkline?: {
    points: PreparedStatSparklinePoint[];
    format?: ValueFormat;
  };
}

// A stat displays a single 0-dimension aggregate, so its value lives at index 0 of the resolved
// column. Returns null if the value can't be resolved (caller falls back to a placeholder).
const firstCell = (data: QueryResponse, ref: string): CellValue | undefined =>
  resolveColumn(data, ref)?.values[0];

// The bucket values a stat's sparkline is plotted over: the single dimension column of the query
// the sparkline's measure came from. A stat's sparkline query is time-bucketed by exactly one
// dimension (that is what makes it a series rather than the stat's own 0-dimension aggregate), so
// the first dimension in the block is the bucket. Returns undefined when the block carries none —
// the caller then falls back to the row index.
const sparklineBuckets = (data: QueryResponse, ref: string): CellValue[] | undefined => {
  const { queryId } = parseOutputFieldRef(ref);
  const block = queryId ? data.queries[queryId] : undefined;
  const bucket = block?.schema.find(c => c.role === 'dimension');
  return bucket ? block?.data[bucket.id] : undefined;
};

// Map a stat ChartConfig + its columnar response into the value / compare / sparkline shape the
// StatRenderer consumes. Returns null for non-stat configs or when the value ref can't be resolved.
export const prepareStat = (
  config: ChartConfig,
  data: QueryResponse,
  ctx?: FieldMetaCtx
): PreparedStat | null => {
  if (config.type !== 'stat') return null;
  const { encoding } = config;

  // Resolved rather than firstCell'd, so the column's descriptor — and with it the response's
  // per-column currency — is in scope for the formatter below. A stat is a 0-dimension aggregate, so
  // its value is still index 0.
  const valueColumn = resolveColumn(data, encoding.value.ref);
  const value = valueColumn?.values[0];
  if (value === undefined) return null;

  const prepared: PreparedStat = {
    value,
    formatValue: getFieldMeta(encoding.value.ref, {
      format: encoding.value.format,
      currency: valueColumn?.descriptor?.currency,
      ctx
    }).format,
    label: encoding.label
  };

  if (encoding.compare) {
    const previous = firstCell(data, encoding.compare.ref);
    // A positive delta is "good" when the metric's good direction is up (the default), and
    // "bad" when it's down. Leave it neutral if the delta couldn't be computed.
    const delta = computeStatDelta(value, previous ?? null, encoding.compare.mode);
    const goodDirection = encoding.compare.goodDirection ?? 'up';
    prepared.compare = {
      delta,
      previous: previous ?? null,
      mode: encoding.compare.mode,
      isGood: delta === null || delta === 0 ? undefined : delta > 0 === (goodDirection === 'up'),
      format: encoding.compare.format
    };
  }

  if (encoding.sparkline) {
    const column = resolveColumn(data, encoding.sparkline.ref);
    if (column) {
      // StatEncoding carries no separate x ref for the sparkline (the value is a single aggregate),
      // so the bucket is recovered from the sparkline query's OWN dimension column — the thing that
      // made the measure a series in the first place. Without it x is a bare row index, which draws
      // the same trend but leaves the tooltip unable to say which bucket it is over.
      const buckets = sparklineBuckets(data, encoding.sparkline.ref);
      prepared.sparkline = {
        points: column.values.map((y, i) => ({ x: buckets?.[i] ?? i, y })),
        format: encoding.value.format
      };
    }
  }

  return prepared;
};

// Currency map keyed by arbitrary string, so a code outside the literal key set can be looked up
// and MISS — which is the point. A miss is either the mixed-currency marker or a real currency this
// package has no formatter for, and both must render as `mixed` rather than as some other currency.
const currencyFormatters: Record<string, (value: number | null, decimals?: number) => string> =
  CURRENCY_FORMATTER_MAP;

// Format a numeric cell for axis ticks / tooltips per its ValueFormat. null is genuinely absent
// (never zero) — show the zeroLabel ("--").
export const formatChartValue = (value: CellValue, format?: ValueFormat): string => {
  if (value === null || value === undefined) return format?.zeroLabel ?? ZERO_LABEL;
  if (typeof value === 'string') return value;

  const { decimals } = format ?? {};
  switch (format?.kind) {
    case 'currency':
      // Mirrors `explicitFormatter` in bpm-types' fieldMeta: an ABSENT code still means "this
      // company's money" and keeps rendering USD, but a code the map doesn't know — the
      // MIXED_CURRENCY_CODE marker, or a real currency with no formatter here — falls to
      // `formatMixedCurrency`. The old `?? formatMoney` fallback conflated the two and stamped `$`
      // on a mixed column, which is the one wrong answer that looks right.
      return (currencyFormatters[format.currencyCode || 'USD'] ?? formatMixedCurrency)(
        value,
        decimals
      );
    case 'percent':
      return toPrettyPercent(value, { decimals });
    case 'integer':
      return formatNumberAsInt(value);
    default:
      return format?.compact ? abbreviateNumber(value, decimals) : formatNumber(value, decimals);
  }
};

// Symbol map keyed by arbitrary string, for the same reason as `currencyFormatters`: a code outside
// the literal key set (the MIXED_CURRENCY_CODE marker, or a currency with no symbol here) must MISS.
const currencySymbols: Record<string, string> = CURRENCY_SYMBOL_MAP;

// Most decimals an axis tick ever shows.
const MAX_AXIS_DECIMALS = 2;

// Tick counts to try, recharts' default (5) first. Axis ticks are computed here with recharts' own
// nice-number algorithm (getNiceTickValues) rather than left to recharts, so the formatter can see
// EVERY tick on an axis when it picks the unit and decimals — a tick formatter only ever sees one
// tick. The other counts are fallbacks for when 5 lands ticks that need more than two decimals
// (0–0.1 by 0.025): 6 gives 0–0.1 by 0.02, which every tick label can state exactly.
const AXIS_TICK_COUNTS = [5, 6, 4];

// Units an axis abbreviates to, largest first. Thousands read as a lowercase "k" on chart axes.
const AXIS_UNITS = [
  { divisor: 1e12, suffix: 'T' },
  { divisor: 1e9, suffix: 'B' },
  { divisor: 1e6, suffix: 'M' },
  { divisor: 1e3, suffix: 'k' },
  { divisor: 1, suffix: '' }
];

// How every tick on one axis is written: a single unit and decimal count for the whole axis, so the
// ticks line up and stay exact ("0.00, 0.75, 1.50") instead of each rounding on its own
// ("0, 0.8, 1.5").
export interface AxisTickScale {
  divisor: number;
  suffix: string;
  decimals: number;
}

const isWhole = (n: number): boolean => Math.abs(n - Math.round(n)) < 1e-9;

// Fewest decimals (up to MAX_AXIS_DECIMALS) that write every value exactly, else the max.
const exactDecimals = (values: number[]): number =>
  [0, 1].find(d => values.every(v => isWhole(v * 10 ** d))) ?? MAX_AXIS_DECIMALS;

export const resolveTickScale = (ticks: number[], format?: ValueFormat): AxisTickScale => {
  // Percent ticks are fractions written x100, never abbreviated.
  if (format?.kind === 'percent') {
    return { divisor: 1, suffix: '', decimals: exactDecimals(ticks.map(t => t * 100)) };
  }
  const maxAbs = Math.max(0, ...ticks.map(t => Math.abs(t)));
  if (maxAbs === 0) return { divisor: 1, suffix: '', decimals: 0 };
  // An axis whose values never go over 1 always shows two decimals ("0.05", "$0.40").
  if (maxAbs <= 1) return { divisor: 1, suffix: '', decimals: MAX_AXIS_DECIMALS };

  const unitIdx = AXIS_UNITS.findIndex(u => maxAbs >= u.divisor);
  let unit = AXIS_UNITS[unitIdx];
  let decimals = exactDecimals(ticks.map(t => t / unit.divisor));
  // When the largest unit needs two decimals (0–1,000 by 250 reads "0.25k"), drop a unit if that
  // writes every tick as a whole number ("250").
  const smaller = AXIS_UNITS[unitIdx + 1];
  const smallerIsWhole = smaller && exactDecimals(ticks.map(t => t / smaller.divisor)) === 0;
  if (decimals === MAX_AXIS_DECIMALS && smallerIsWhole) {
    unit = smaller;
    decimals = 0;
  }
  // Unabbreviated money shows cents in pairs: "$1.50", not "$1.5".
  if (format?.kind === 'currency' && unit.divisor === 1 && decimals === 1) {
    decimals = MAX_AXIS_DECIMALS;
  }
  return { ...unit, decimals };
};

// Format a value-axis tick (Y, X when horizontal, or a scatter's numeric X). Ticks are abbreviated
// ("66k", "1.5M") so the gutter stays narrow: it is sized to its measured content
// (useDynamicYAxisWidth), but that measurement lags a render behind a data change, so long
// magnitudes like "66,000" could still get clipped mid-transition. The full-precision value still
// shows in the tooltip (formatChartValue).
//
// `scale` is the axis' shared unit + decimals (resolveTickScale over all of its ticks); without one
// the tick is scaled on its own. Currency ticks sit behind the currency's symbol ("$66k",
// "SEK kr66k"); a code with no symbol — a mixed-currency column or an unknown currency — gets the
// "mixed " prefix formatMixedCurrency uses, never a guessed "$". Zero is always a bare "0" ("$0",
// "0%"), whatever the axis' decimals.
export const formatAxisTick = (
  value: CellValue,
  format?: ValueFormat,
  axisScale?: AxisTickScale
): string => {
  if (typeof value !== 'number') return formatChartValue(value, format);
  const scale = axisScale ?? resolveTickScale([value], format);
  const sign = value < 0 ? '-' : '';
  const isZero = value === 0;
  if (format?.kind === 'percent') {
    return `${sign}${formatNumber(Math.abs(value) * 100, isZero ? 0 : scale.decimals)}%`;
  }
  const prefix =
    format?.kind === 'currency' ? (currencySymbols[format.currencyCode || 'USD'] ?? 'mixed ') : '';
  if (isZero) return `${prefix}0`;
  return `${sign}${prefix}${formatNumber(Math.abs(value) / scale.divisor, scale.decimals)}${scale.suffix}`;
};

// One value axis, resolved from the series bound to it.
export interface ValueAxis {
  format?: ValueFormat;
  // The axis title: the distinct metric names plotted on it, comma-separated ("Spend, Revenue").
  label: string;
  // The axis' ticks, which also fix its domain ([first, last]). Undefined when there's no numeric
  // data to place them from, leaving recharts' default axis.
  ticks?: number[];
  tickScale: AxisTickScale;
}

export interface ValueAxes {
  dualAxis: boolean;
  // The primary value axis (the left axis, or the bottom axis when horizontal).
  left: ValueAxis;
  right?: ValueAxis;
}

interface ValueAxisOpts {
  horizontal: boolean;
  // Whether bar/area series stack (encoding.stack / grouping 'stacked'). Lines never stack.
  stacked: boolean;
  // The chart's mark, for series with no per-series override.
  baseMark: SeriesMark;
}

const STACKED_MARKS: SeriesMark[] = ['bar', 'area'];

// [min, max] over every finite number in `values`, or null when there are none.
const numericExtent = (values: CellValue[]): [number, number] | null => {
  const nums = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (nums.length === 0) return null;
  return [Math.min(...nums), Math.max(...nums)];
};

// The data range an axis has to cover. Stacked bar/area series are measured by their running sums
// in series order — each layer's top and bottom, matching recharts' default (stackOffset "none")
// stacking — and every other series by its own values.
const seriesExtent = (
  rows: Record<string, CellValue>[],
  series: PreparedSeries[],
  { stacked, baseMark }: ValueAxisOpts
): [number, number] | null => {
  const isStacked = (s: PreparedSeries): boolean =>
    stacked && !s.overlay && STACKED_MARKS.includes(s.mark ?? baseMark);
  const values: CellValue[] = series
    .filter(s => !isStacked(s))
    .flatMap(s => (s.points ?? rows).map(row => row[s.key]));
  const stackedSeries = series.filter(isStacked);
  if (stackedSeries.length > 0) {
    rows.forEach(row => {
      let running = 0;
      stackedSeries.forEach(s => {
        const v = row[s.key];
        if (typeof v === 'number' && Number.isFinite(v)) {
          running += v;
        }
        values.push(running);
      });
    });
  }
  return numericExtent(values);
};

// Nice ticks spanning `extent`, preferring a set every label can write exactly in two decimals.
// Value axes start from zero, matching recharts' default [0, 'auto'] domain; a scatter's numeric x
// spans just its data, matching the ['auto', 'auto'] it used before.
const niceTicks = (
  extent: [number, number] | null,
  includeZero: boolean,
  format?: ValueFormat
): number[] | undefined => {
  if (!extent) return undefined;
  const [min, max] = includeZero ? [Math.min(0, extent[0]), Math.max(0, extent[1])] : extent;
  const candidates = AXIS_TICK_COUNTS.map(count => getNiceTickValues([min, max], count, true));
  const shown = (t: number): number => (format?.kind === 'percent' ? t * 100 : t);
  const writesExactly = (ticks: number[]): boolean =>
    ticks.every(t => isWhole(shown(t) * 10 ** MAX_AXIS_DECIMALS));
  return candidates.find(writesExactly) ?? candidates[0];
};

const axisLabel = (series: PreparedSeries[]): string =>
  [...new Set(series.map(s => s.metricLabel))].join(', ');

const buildValueAxis = (
  rows: Record<string, CellValue>[],
  series: PreparedSeries[],
  opts: ValueAxisOpts
): ValueAxis => {
  const { format } = series[0] ?? {};
  const ticks = niceTicks(seriesExtent(rows, series, opts), true, format);
  return {
    format,
    label: axisLabel(series),
    ticks,
    tickScale: resolveTickScale(ticks ?? [], format)
  };
};

// Resolve the value axes for a prepared chart. A right axis is drawn only when series sit on BOTH
// sides — if every series is right-bound (e.g. a metric picker on a dual-axis line chart narrowed to
// its right-side metric), they share one axis instead of leaving an empty left gutter. Each axis is
// built from the series actually bound to it, never blindly from series[0], so `y` order can't put
// currency ticks (or the wrong metric name) on the wrong axis.
export const resolveValueAxes = (
  prepared: Pick<PreparedGraphicChart, 'rows' | 'series'>,
  opts: ValueAxisOpts
): ValueAxes => {
  const { rows, series } = prepared;
  // Horizontal bars have a single value axis, so `side` doesn't apply.
  const leftSeries = opts.horizontal ? series : series.filter(s => s.side !== 'right');
  const rightSeries = opts.horizontal ? [] : series.filter(s => s.side === 'right');
  if (leftSeries.length === 0 || rightSeries.length === 0) {
    return { dualAxis: false, left: buildValueAxis(rows, series, opts) };
  }
  return {
    dualAxis: true,
    left: buildValueAxis(rows, leftSeries, opts),
    right: buildValueAxis(rows, rightSeries, opts)
  };
};

// A scatter's numeric x axis: x is a metric, so it gets value-axis ticks and its metric's name.
export const resolveNumericXAxis = (prepared: PreparedGraphicChart): ValueAxis => {
  const xValues = prepared.rows.map(row => row[prepared.xKey]);
  const ticks = niceTicks(numericExtent(xValues), false, prepared.xFormat);
  return {
    format: prepared.xFormat,
    label: prepared.xLabel ?? '',
    ticks,
    tickScale: resolveTickScale(ticks ?? [], prepared.xFormat)
  };
};

// A color split's tooltip rows: largest value at the hovered x first, then the overlay lines in
// their declared order. Other charts keep draw order (mixed units, or comparison pairs).
export const orderTooltipEntries = <T extends { dataKey?: string | number; value?: unknown }>(
  entries: T[],
  prepared: PreparedGraphicChart
): T[] => {
  if (!prepared.colorSplit) return entries;
  const overlayKeys = new Set(prepared.series.filter(s => s.overlay).map(s => s.key));
  const isOverlay = (entry: T): boolean => overlayKeys.has(String(entry.dataKey));
  const hasAmount = (entry: T): number => Number(typeof entry.value === 'number');
  const amount = (entry: T): number => (typeof entry.value === 'number' ? entry.value : 0);
  const splits = entries
    .filter(e => !isOverlay(e))
    .sort((a, b) => hasAmount(b) - hasAmount(a) || amount(b) - amount(a));
  return [...splits, ...entries.filter(isOverlay)];
};

// Longest a title may be, in px, before it is cut with an ellipsis; estimated from the font size
// (SVG text can't be measured before it renders). The full title stays available on hover.
const AXIS_TITLE_CHAR_WIDTH_RATIO = 0.6;

export const truncateAxisTitle = (title: string, maxWidth: number, fontSize: number): string => {
  const maxChars = Math.floor(maxWidth / (fontSize * AXIS_TITLE_CHAR_WIDTH_RATIO));
  if (title.length <= maxChars) return title;
  if (maxChars <= 1) return '…';
  return `${title.slice(0, maxChars - 1).trimEnd()}…`;
};

// Format an x-axis tick. Dates arrive as ISO strings ("YYYY-MM-DD"); show a compact "M/D"
// without constructing a Date (avoids UTC-vs-local off-by-one shifts).
export const formatXTick = (value: CellValue, prepared: PreparedGraphicChart): string => {
  if (value === null || value === undefined) return '';
  if (prepared.xIsDate && typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (match) return `${Number(match[2])}/${Number(match[3])}`;
  }
  return typeof value === 'number' ? formatChartValue(value, prepared.xFormat) : value;
};

// Format a comparison window's date for the tooltip. Unlike the axis tick this keeps the year —
// year-over-year windows would otherwise read as the same day.
export const formatComparisonDate = (value: CellValue): string => {
  if (typeof value !== 'string') return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${Number(match[2])}/${Number(match[3])}/${match[1].slice(2)}` : value;
};

// A user's transient click-to-sort selection. Null = no user sort, i.e. fall back to prepareTable's
// default date pre-sort. Table sort is transient view state — never written back to the saved config.
export type ColumnSort = { key: string; dir: 'asc' | 'desc' } | null;

// Reorder rows by a user-selected column. MUST be called upstream of prepareTable's rowLimit slice:
// sorting an already-truncated window reorders an arbitrary slice while presenting it as top-N.
export const sortRows = (
  rows: Record<string, CellValue>[],
  columns: PreparedTableColumn[],
  sort: ColumnSort
): Record<string, CellValue>[] => {
  if (!sort) return rows;
  const col = columns.find(c => c.key === sort.key);
  if (!col) return rows;

  const mult = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[sort.key];
    const bv = b[sort.key];
    // Empty cells sink in BOTH directions. Decided BEFORE the direction multiplier is applied, so a
    // descending sort doesn't float them to the top.
    if (av === null) return bv === null ? 0 : 1;
    if (bv === null) return -1;
    // Dispatch on the COLUMN, not per cell — this is what keeps a mixed-type column from producing
    // silent ties. Tradeoff: a dimension holding numbers compares as text ('10' before '9'). Accepted.
    if (col.isMetric && typeof av === 'number' && typeof bv === 'number') return (av - bv) * mult;
    // Ordinal compare, matching the existing sortRowsByDateKey in this file (:224-238): ISO date
    // strings sort correctly lexically, and localeCompare costs roughly 10x per comparison — material
    // on a 20k-row table.
    const as = String(av);
    const bs = String(bv);
    if (as < bs) return -1 * mult;
    return as > bs ? mult : 0;
  });
};

// The synthetic `date` dimension. Excluded from search at EVERY grouping, daily included: it has no
// registry column (`isDimensionColumn('date')` is false), so the server can build no predicate for it
// and `searchPredicate` THROWS rather than skipping it — deliberately, since silently dropping a
// dimension the browser IS filtering by would total a wider population than the rows shown
// (bpm-lambdas dashboarding/filterClause.ts:196-203). Its displayed values are bucketing expressions
// anyway, so they would not round-trip. Revisit only if bucketed-date search lands.
const DATE_DIMENSION_ID = 'date';

/**
 * Which dimensions a table's search box narrows by, in the two forms the two halves of the parity
 * contract need: registry ids for `QuerySearch.dimensions` (the server ORs them into its predicate)
 * and prepared-row keys for the browser predicate (it ORs them over the same values).
 *
 * Two parallel lists rather than pairs: neither half ever needs the other's form, and a comparison
 * table can surface one dimension under two refs, which pairing would force a choice about.
 */
export interface TableSearchScope {
  dimensionIds: DimensionId[];
  rowKeys: string[];
}

/**
 * Derive the searchable set from a table config + its prepared columns.
 *
 * A dimension qualifies when the table DISPLAYS it (the browser needs its raw values to evaluate the
 * predicate), its query GROUPS BY it, and it is not the synthetic `date`. The grouping requirement is
 * what makes this analytics-safe without a new check: `validateQuery` already rejects a grouped
 * dimension that an analytics source cannot supply, so anything reaching us has a real column.
 *
 * Custom segments are deliberately included. The server resolves them via `customSegmentExpr`, but the
 * browser cannot see the company's custom-segment list — it lives in the server-side company context —
 * so the rule is stated as an exclusion. In a config that passed validation, every dimension id other
 * than `date` is either a registry dimension or a company custom segment, and the server resolves both.
 *
 * An empty result means the table has nothing searchable, and the caller should not offer a search box
 * at all: narrowing rows the total cannot be scoped by is the plausible-wrong-number this design exists
 * to remove.
 */
export const getTableSearchScope = (
  config: ChartConfig,
  columns: PreparedTableColumn[]
): TableSearchScope => {
  if (config.type !== 'table') return { dimensionIds: [], rowKeys: [] };

  const dimensionsByQueryId = new Map(config.queries.map(q => [q.queryId, q.dimensions]));
  const dimensionIds: DimensionId[] = [];
  const rowKeys: string[] = [];

  columns.forEach(col => {
    if (col.isMetric) return;
    const { queryId, colId } = parseOutputFieldRef(col.key);
    if (colId === DATE_DIMENSION_ID) return;
    if (!dimensionsByQueryId.get(queryId)?.some(dim => dim.id === colId)) return;
    rowKeys.push(col.key);
    if (!dimensionIds.includes(colId)) {
      dimensionIds.push(colId);
    }
  });

  return { dimensionIds, rowKeys };
};

/**
 * Normalize a raw search-box value exactly as the server does — trim, then lowercase
 * (`filterClause.ts:185,190`). An empty result means "no search" on both sides: the server emits no
 * clause, and the caller must not send one. Trimming is load-bearing, not tidiness: a term with
 * surrounding spaces would otherwise narrow the body without narrowing the total.
 */
export const normalizeSearchTerm = (searchTerm: string): string => searchTerm.trim().toLowerCase();

/**
 * The browser half of the FE/server search parity contract.
 *
 * The server emits `contains(lower(cast(coalesce(col,'') as varchar)), '<lowered term>')` per searched
 * dimension, ORed. `contains()` was chosen over `like` precisely so this is a plain substring test with
 * no pattern semantics — `%` and `_` are literal on both sides — making the two EXACTLY equal rather
 * than approximately so.
 *
 * `?? ''` IS the server's `coalesce`, not a defensive habit. `String(null)` is the string `"null"`, so
 * without it a search for "null" would match every NULL-backed row here and none server-side, and the
 * footer would contradict the rows above it.
 */
export const rowMatchesSearch = (
  row: Record<string, CellValue>,
  rowKeys: string[],
  loweredTerm: string
): boolean =>
  rowKeys.some(key =>
    String(row[key] ?? '')
      .toLowerCase()
      .includes(loweredTerm)
  );

/**
 * Narrow displayed rows to those matching the search across the table's searchable DIMENSION columns.
 *
 * Replaces the old `filterRows`, which tested EVERY cell — metrics included — against raw values, so
 * typing `4` matched a ROAS of `4.127…` while `$`/`%`/`1,234` matched nothing (formatting is applied at
 * render). That was never a designed feature and it cannot be mirrored server-side: scoping the totals
 * query by it would mean filtering on aggregated values that do not exist before the `GROUP BY`.
 */
export const filterRowsBySearch = (
  rows: Record<string, CellValue>[],
  rowKeys: string[],
  searchTerm: string
): Record<string, CellValue>[] => {
  const loweredTerm = normalizeSearchTerm(searchTerm);
  if (!loweredTerm || rowKeys.length === 0) return rows;
  return rows.filter(row => rowMatchesSearch(row, rowKeys, loweredTerm));
};

// The table's totals queries as they are sent to the server: with the detail query's metric filters
// copied in (rollup grain) when derivation applies. Empty when any configured totals query is missing.
const derivedTotalsQueries = (config: TableChartConfig): QuerySpec[] => {
  const { totalsQueryId, totalsQueryIds } = config.encoding;
  const totalsIds = totalsQueryIds ? [...new Set(Object.values(totalsQueryIds))] : [totalsQueryId];
  const { queries } = withDerivedTotals(config);
  const totals = totalsIds.flatMap(id => queries.filter(query => query.queryId === id));
  return totals.length === totalsIds.length ? totals : [];
};

/**
 * The request body for a search-scoped grand total: the chart's OWN 0-dimension totals queries (one, or
 * one per source with `totalsQueryIds`), re-sent with the same predicate the browser applied to the rows.
 *
 * Re-running `qT` rather than aggregating in the browser is the whole design. A ratio total stays
 * `sum(num)/sum(den)` over exactly the matching population, weighted metrics and medians and
 * distinct-counts keep working, and there is only ever one aggregation implementation to keep correct.
 *
 * Sending only the totals queries is deliberate: each comes back as its own block, so they need no
 * `merge`, and carrying the chart's `comparison` intent across would name queries this config lacks.
 *
 * The term goes out TRIMMED BUT NOT LOWERCASED — the server lowercases both sides of its own
 * comparison, so pre-lowering here would just hide where that happens.
 *
 * `null` means "issue no request": no grand total configured, nothing searchable, or an empty term.
 * Zero MATCHING ROWS is the caller's call rather than this function's — the browser already knows the
 * answer is "nothing to total" and must not spend a round trip being told so.
 */
export const buildSearchTotalsConfig = (
  config: ChartConfig,
  scope: TableSearchScope,
  searchTerm: string
): QueryConfig | null => {
  if (config.type !== 'table') return null;
  const { showGrandTotal, totalsQueryId, totalsQueryIds } = config.encoding;
  const term = searchTerm.trim();
  if (!showGrandTotal || (!totalsQueryId && !totalsQueryIds)) return null;
  if (!term || scope.dimensionIds.length === 0) return null;
  // No footer will render, so don't pay a round trip (and a Redis entry) per search term for it.
  if (totalsIgnoresMetricFilters(config.queries, config.encoding, config.merge)) return null;

  // Derive BEFORE adding `search`: derivation compares `search` between the queries, so adding it
  // first would make derivation refuse.
  const totalsQueries = derivedTotalsQueries(config);
  if (totalsQueries.length === 0) return null;

  const search = { term, dimensions: scope.dimensionIds };
  return { queries: totalsQueries.map(query => ({ ...query, search })) };
};

// The chart config plus the dashboard filters the server applied: it appends the same nodes to the
// end of every query's filters, so they are what follows the chart query's own filters.
export const withAppliedDashboardFilters = (
  config: ChartConfig,
  data: QueryResponse
): ChartConfig => {
  const resolved = data.dashboardFilterResult?.resolvedConfig?.queries ?? [];
  const chartQuery = config.queries.find(q => resolved.some(r => r.queryId === q.queryId));
  if (!chartQuery) return config;
  const own = chartQuery.filters ?? [];
  const ran = resolved.find(r => r.queryId === chartQuery.queryId)?.filters ?? [];
  if (!equals(ran.slice(0, own.length), own)) return config;
  const appended = ran.slice(own.length);
  if (appended.length === 0) return config;
  return {
    ...config,
    queries: config.queries.map(q => ({ ...q, filters: [...(q.filters ?? []), ...appended] }))
  };
};

/**
 * True when a search-scoped grand total would total the wrong rows, so the footer must be hidden
 * while a search is active.
 *
 * The server's grain query inherits the totals query's `search`, so it tests the metric filter on the
 * SEARCHED rows only. The table, though, filtered on the FULL data and searches in the browser. The
 * two agree only when every derived filter's grain contains every searched dimension. Example: rows
 * at campaign x channel, grain [campaign], search "social": campaign A can pass the threshold on all
 * its channels but fail on Social alone, so the total would drop the Social row the table still shows.
 */
export const searchTotalsUnavailable = (config: ChartConfig, scope: TableSearchScope): boolean => {
  if (config.type !== 'table') return false;
  return derivedTotalsQueries(config)
    .flatMap(query => query.metricFilters ?? [])
    .some(f => !scope.dimensionIds.every(id => (f.grain ?? []).some(g => g.id === id)));
};

/**
 * True when the chart's selected metric is computed WITHOUT a KPI — a delivery metric like spend,
 * impressions or CPC — so changing the KPI cannot change what's plotted.
 *
 * Only decides for ids the SCP registry knows. `colOrMetricRequiresKpi` answers false for any id it
 * doesn't recognise, custom-metric ids included, so the `getScpConfig` membership check is what
 * keeps an unknown id from being read as "delivery-only". It is deliberately not `getConfig`, which
 * also falls back to the KPI Volumes registry: a KPI Volumes metric like `rawkpi_volume` counts the
 * selected KPI itself, so it always depends on it. A custom metric's KPI dependence lives in its
 * `requires` list (see `metricRequiresKpi` in bpm-types' validateQuery), which the frontend's
 * FieldMetaCtx does not carry — so custom metrics deliberately fall through as "can't tell", and
 * the caller leaves the KPI control alone rather than disabling one that does matter.
 */
export const metricIgnoresKpi = (colId: string): boolean =>
  Boolean(getScpConfig(colId as ColumnOrMetricName)) && !colOrMetricRequiresKpi(colId);
